/**
 * `companies.remote_work_policy` holds a CSV of lowercase enum values.
 *
 * CSV rather than a new column or a jsonb array because the column already
 * exists and already holds single values written by the LinkedIn import and by
 * the seeds ("Remote", "Hybrid"): a defensive parse makes every one of those
 * readable for free, with no data migration.
 */
export const REMOTE_POLICIES = ["remote", "hybrid", "onsite"] as const;

export type RemotePolicy = (typeof REMOTE_POLICIES)[number];

const POLICY_SET: ReadonlySet<string> = new Set(REMOTE_POLICIES);

/** "On-site", "ON SITE" and "onsite" are the same answer typed three ways. */
const normalize = (token: string) =>
  token
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

/**
 * Reads whatever is in the column. Unknown tokens are dropped rather than
 * shown: a value nobody can render is worse than a missing one, and this
 * column has legacy content nobody has migrated.
 */
export const parseRemotePolicies = (
  raw: string | null | undefined,
): RemotePolicy[] => {
  if (!raw) return [];
  const seen = new Set<RemotePolicy>();
  for (const token of raw.split(",")) {
    const value = normalize(token);
    if (POLICY_SET.has(value)) seen.add(value as RemotePolicy);
  }
  // Canonical order, not the order they were stored in, so the same selection
  // always serialises to the same string.
  return REMOTE_POLICIES.filter((policy) => seen.has(policy));
};

/**
 * `null` for an empty selection, never `""`: an empty string is a value the
 * public page would have to special-case, a `null` is simply absent (AC 19).
 */
export const serializeRemotePolicies = (
  policies: readonly RemotePolicy[],
): string | null => {
  const ordered = REMOTE_POLICIES.filter((policy) => policies.includes(policy));
  return ordered.length > 0 ? ordered.join(",") : null;
};

/**
 * What a stored value *means*, as the column would hold it.
 *
 * The round trip through the parse is the point: "Remote" and "remote" are one
 * answer written two ways, and the settings page has to be able to say so on
 * **both** sides of its dirty check. Compared raw, a legacy value never equals
 * the canonical string the toggles produce — so two clicks back to the starting
 * point would leave the save bar, and the leave guard with it, armed for good
 * over a page nobody changed (FLOW EP6).
 */
export const canonicalRemotePolicy = (
  raw: string | null | undefined,
): string | null => serializeRemotePolicies(parseRemotePolicies(raw));
